<template>
  <div class="seletor-imagem">
    <label v-if="label" class="seletor-imagem__label" :title="label">
      {{ label }}
    </label>

    <div
      class="upload-arquivo"
      :class="{ 'upload-arquivo--preenchido': !!value }"
      v-on:click="abrirGerenciador"
      :title="value ? 'Clique para escolher outra imagem' : 'Clique para escolher a imagem'"
    >
      <!-- Área de mídia com altura fixa nos dois estados: sem isso a caixa vazia
           fica mais baixa que a preenchida, e uma imagem só em desktop ou só em
           mobile deixaria as duas colunas desalinhadas. -->
      <div class="upload-arquivo__midia">
        <img v-if="value" :src="value" :alt="nomeArquivo" />
        <div v-else class="upload-arquivo__icone">
          <fa-icon icon="image" />
        </div>
      </div>
      <div class="upload-arquivo__nome" :title="nomeArquivo">
        {{ nomeArquivo || "Selecionar imagem" }}
      </div>
      <div class="upload-arquivo__dica">
        {{ value ? "Clique para substituir" : dica }}
      </div>
    </div>

    <!-- Colar endereço continua possível: era como o campo antigo funcionava e
         resolve o caso de imagem hospedada fora do gerenciador. -->
    <input
      class="form-control form-control-sm seletor-imagem__url"
      :value="value"
      :placeholder="placeholder"
      v-on:input="$emit('input', $event.target.value)"
    />
  </div>
</template>

<script>
import setup from "@/config";
import { uuid } from "vue-uuid";

/**
 * Escolha de imagem pelo gerenciador de arquivos, na mesma linguagem visual da
 * área de anexo de certificado do cadastro de adquirentes.
 *
 * O gerenciador abre em outra janela e devolve a escolha por postMessage, no
 * formato { id, contents: [{ url }] }, casando pelo id que enviamos em
 * target_id. Mesmo protocolo do componente ImagePicker — se ele mudar de lado
 * da API, os dois precisam acompanhar.
 */
export default {
  name: "SeletorImagem",
  props: {
    /** Endereço da imagem (v-model). */
    value: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    dica: {
      type: String,
      default: "Escolher no gerenciador de arquivos",
    },
    placeholder: {
      type: String,
      default: "Ou cole o endereço da imagem",
    },
  },
  data() {
    return {
      urlGerenciador: `${setup.api}/admin/arquivos`,
      token: setup.token(),
      id: uuid.v4(),
    };
  },
  computed: {
    nomeArquivo() {
      if (!this.value) {
        return "";
      }

      const semParametros = String(this.value).split("?")[0];
      const nome = semParametros.split("/").pop();

      try {
        return decodeURIComponent(nome);
      } catch (e) {
        return nome;
      }
    },
  },
  methods: {
    abrirGerenciador() {
      const largura = 900;
      const altura = 600;
      const esquerda = window.screenX + (window.outerWidth - largura) / 2;
      const topo = window.screenY + (window.outerHeight - altura) / 2;

      window.open(
        `${this.urlGerenciador}?token=${this.token}&target_id=${this.id}`,
        "FileManager",
        `scrollbars=yes, width=${largura}, height=${altura}, top=${topo}, left=${esquerda}`
      );
    },
    imagemEscolhida(evento) {
      const dados = evento.data;

      if (dados && dados.id === this.id && dados.contents && dados.contents[0]) {
        this.$emit("input", dados.contents[0].url);
      }
    },
  },
  mounted() {
    window.addEventListener("message", this.imagemEscolhida);
  },
  beforeDestroy() {
    window.removeEventListener("message", this.imagemEscolhida);
  },
};
</script>

<style scoped>
/* Rótulo em uma linha só: os textos de desktop e mobile têm comprimentos
   diferentes e, quebrando em duas linhas em uma coluna e não na outra, as caixas
   voltariam a ficar desalinhadas. O texto completo fica no title. */
.seletor-imagem__label {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 3px;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Mesmos valores da área de anexo do cadastro de adquirentes. */
.upload-arquivo {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 14px 12px;
  border: 2px dashed #dddce9;
  border-radius: 10px;
  background: #fafbfc;
  cursor: pointer;
  text-align: center;
  transition: all 0.15s ease;
}

.upload-arquivo:hover {
  border-color: #3a3e81;
  background: #f7f7fc;
}

.upload-arquivo--preenchido {
  border-style: solid;
  border-color: #b9e7be;
  background: #f5fbf6;
}

.upload-arquivo__icone {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f2f2f8;
  color: #3a3e81;
  margin-bottom: 2px;
}

/* Altura fixa e igual nos dois estados. No lugar do ícone de confirmação da
   versão de certificado entra a própria imagem: em banner, ver o que foi
   escolhido vale mais que um sinal de ok. */
.upload-arquivo__midia {
  width: 100%;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  overflow: hidden;
  flex-shrink: 0;
}

.upload-arquivo__midia img {
  max-width: 100%;
  max-height: 120px;
  object-fit: contain;
  border-radius: 4px;
}

/* Nome em uma linha só: nome de arquivo comprido quebraria em duas e voltaria a
   descasar a altura das colunas. O texto completo fica no title. */
.upload-arquivo__nome {
  font-size: 12px;
  font-weight: 600;
  color: #344054;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.upload-arquivo__dica {
  font-size: 11px;
  color: #98a2b3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.seletor-imagem__url {
  margin-top: 6px;
  font-size: 12px;
}
</style>
